import jsqr from 'jsqr';
import { LitElement, html, css, nothing } from '~/lib/bn-lit-element';
import '../common/bn-icon';
import '../common/bn-banner';

export function dataToBlob(dataUri) {
  const [, type, base64, data] = dataUri.match(/data:(image\/\w+)(;base64)?,(.*)/);
  const bytes = base64 ? atob(data) : unescape(data);
  const array = [];
  for (let i = 0; i < bytes.length; ++i) {
    array.push(bytes.charCodeAt(i));
  }
  return new Blob([new Uint8Array(array)], { type });
}

class BnCamera extends LitElement {
  constructor() {
    super();
    this.loading = false;
    this.imageData = undefined;

    this.tick = this.tick.bind(this);
  }

  static get properties() {
    return {
      loading: { type: Boolean, attribute: false },
      imageData: { type: String, attribute: false },
      error: { type: String, attribute: false },
    };
  }

  static get styles() {
    return css`
      :host {
        display: block;
        position: relative;
        height: 100%;
      }
      img,
      canvas {
        max-width: 100%;
        max-height: 400px;
      }
      .button-box {
        position: absolute;
        bottom: 0;
        width: 100%;
        display: flex;
        justify-content: center;
      }
      .button {
        border: 2px solid white;
        border-radius: 50%;
        height: 3.5rem;
        width: 3.5rem;
        padding: 2px;
        background: transparent;
        margin-bottom: 2rem;
      }
      .button button {
        font-size: 1.5rem;
        line-height: 1.5rem;
        height: 3.5rem;
        width: 3.5rem;
        border: none;
        border-radius: 50%;
        background-color: white;
        color: var(--blue-50);
      }
    `;
  }

  render() {
    return html`
      <canvas></canvas>
      ${this.error ? html`<bn-banner severity="critical">${this.error}</bn-banner>` : nothing}
      ${this.renderButton()}
    `;
  }

  renderButton() {
    return html`
      <div class="button-box">
        <span class="button">
          <button>
            <bn-icon name="fa fa-camera" @click="${this.capture}"></bn-icon>
          </button>
        </span>
      </div>
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.stop();
  }

  firstUpdated() {
    this.canvasElement = this.shadowRoot.querySelector('canvas');
    this.canvasContext = this.canvasElement.getContext('2d');
    this.start();
  }

  // this is a hack for certain devices with many cameras ie. Galaxy S10 plus
  async getCompatibleDeviceId() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputDevices = devices.filter(dev => dev.kind === 'videoinput');

    // there must be exactly 4 video devices
    if (inputDevices.length !== 4) {
      return;
    }

    // we want the input device on the back numbered 0
    const dev0 = inputDevices.find(dev => dev.label.includes('0,') && dev.label.includes('back'));

    if (!dev0) {
      return;
    }

    return dev0.deviceId;
  }

  async start() {
    this.scanning = true;
    this.imageData = undefined;

    if (this.video) {
      this.video.play();
      return;
    }

    try {
      this.loading = true;

      const deviceId = await this.getCompatibleDeviceId();
      const params = deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' };

      this.video = document.createElement('video');
      this.video.srcObject = await navigator.mediaDevices.getUserMedia({ video: params });
      this.video.setAttribute('playsinline', true); // required to tell iOS safari we don't want fullscreen
      this.video.play();
      window.requestAnimationFrame(this.tick);
    } catch (ex) {
      this.loading = false;
      this.scanning = false;
      this.video = null;
      this.error = `There was a problem accessing device camera:\n ${ex.message}`;
    }
  }

  async tick() {
    const { video } = this;

    if (!video) {
      return;
    }

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      this.loading = false;
      this.draw();
    }

    window.requestAnimationFrame(this.tick);
  }

  draw() {
    const { canvasElement, canvasContext, video } = this;
    canvasElement.height = video.videoHeight;
    canvasElement.width = video.videoWidth;
    canvasContext.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
  }

  stop() {
    this.scanning = false;
    if (!this.video) {
      return;
    }
    this.canvasContext.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    this.canvasElement.height = 0;
    this.video.srcObject.getTracks().forEach(track => track.stop());
    this.video = null;
  }

  capture() {
    const { canvasElement } = this;
    const data = canvasElement.toDataURL('image/jpeg');
    this.imageData = data;
    this.stop();
    this.dispatchEvent(new CustomEvent('capture', { detail: { data } }));
  }
}

customElements.define('bn-camera', BnCamera);

// ----- QR Code Scanner

const HIGHLIGHT_COLOUR = '#FF3B58';
const LOCATION_MARKER_TIMEOUT_MS = 1200;
const THROTTLE_QR = 5;

class QrCamera extends BnCamera {
  static get properties() {
    return {
      qrData: { type: String, attribute: false },
      qrLocation: { type: Object, attribute: false },
    };
  }

  renderButton() {
    return nothing;
  }

  start() {
    super.start();
    this.frame = 0;
  }

  draw() {
    super.draw();
    if ((++this.frame % THROTTLE_QR) === 0) {
      this.forceFocusCamera();
      this.parseQRCode();
    }
    this.highlightQRCode();
  }

  drawLine(begin, end, color) {
    const { canvasContext } = this;
    canvasContext.beginPath();
    canvasContext.moveTo(begin.x, begin.y);
    canvasContext.lineTo(end.x, end.y);
    canvasContext.lineWidth = 4;
    canvasContext.strokeStyle = color;
    canvasContext.stroke();
  }

  forceFocusCamera() {
    const videoTrack = this.video.srcObject.getVideoTracks()[0];
    if (typeof videoTrack.getCapabilities === 'function' && videoTrack.getCapabilities().focusMode && videoTrack.getCapabilities().focusMode.includes('single-shot')) {
      videoTrack.applyConstraints({ advanced: [{ focusMode: 'single-shot' }] });
    }
  }

  parseQRCode() {
    const { canvasContext, canvasElement } = this;
    const imageData = canvasContext.getImageData(0, 0, canvasElement.width, canvasElement.height);
    const { data, location } = jsqr(imageData.data, imageData.width, imageData.height) || {};

    this.qrLocation = location;
    clearTimeout(this.locationTimeout);
    this.locationTimeout = setTimeout(() => this.qrLocation = null, LOCATION_MARKER_TIMEOUT_MS);

    if (data === this.qrData) {
      return;
    }

    this.qrData = data;
    this.dispatchEvent(new CustomEvent('data', { detail: { data } }));
  }

  highlightQRCode() {
    if (!this.qrLocation) {
      return;
    }
    const { topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner } = this.qrLocation;
    this.drawLine(topLeftCorner, topRightCorner, HIGHLIGHT_COLOUR);
    this.drawLine(topRightCorner, bottomRightCorner, HIGHLIGHT_COLOUR);
    this.drawLine(bottomRightCorner, bottomLeftCorner, HIGHLIGHT_COLOUR);
    this.drawLine(bottomLeftCorner, topLeftCorner, HIGHLIGHT_COLOUR);
  }
}

customElements.define('qr-camera', QrCamera);
